import React, { useMemo } from 'react';

import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogTitle,
  ActionableDialogContent,
  ActionableDialogActions,
  ContainerProps,
} from '@components/Dialog';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { SpinnerModal } from '@components/SpinnerModal';
import { VerticalSpace } from '@components/VerticalSpace';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { useBookingInquiryViewerCreate } from '@hooks/mutation/useBookingInquiryViewerCreate';
import { useBookingInquiryViewerDelete } from '@hooks/mutation/useBookingInquiryViewerDelete';
import { useUser } from '@hooks/queries/useUser';
import { InquiryDetail, InquiryViewer, inquiryViewerAddForm } from '@models/BookingInquiry';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { CodeError } from '@utils/errors';
import { createValidate } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { Field, Formik } from 'formik';
import { unwrap } from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: '予約を共有する',
    emailInputPlaceholder: '共有先のメールアドレスを入力してください',
    share: '共有する',
    limitation: '共有先はD2Dシステムに登録まではこの予約ページにアクセスできません。',
    followers: '共有先',
    copyShareLink: 'リンクをコピー',
    author: '作成者',
    delete: '削除',
    copyShareLinkSuccess: 'リンクをコピーしました。',
    shareWithEmailSuccess: '共有先を追加しました。',
    notRegistered: '（未登録）',
    viewerCreateErrors: () => ({
      ONESELF: '関係者は既に共有されました。',
      REPEATED: '相手は既に共有されました。',
    }),
    creatorOnly: '作成者以外は共有できませんのでご了承ください。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.middleText,
  },
  title: {
    ...common.dialogTitleText,
  },
  emailInputContainer: {
    ...common.flexInline,
  },
  emailInput: {
    ...common.smallerText,
    ...common.fontWeight500,
    lineHeight: 1,
    padding: '7px 7px !important',
    outline: 'none',
    borderWidth: 'thin 0 thin thin',
    borderColor: theme.palette.primary.main,
    borderRadius: 0,
    width: 302,
    flexShrink: 1,
    borderStyle: 'solid',
    '&:disabled': {
      opacity: 0.5,
    },
  },
  shareButton: {
    ...common.middleText,
    background: theme.palette.primary.main,
    color: theme.palette.common.white,
    paddingLeft: 36,
    paddingRight: 36,
    whiteSpace: 'nowrap',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  shareLimitation: {
    ...common.exSmallerText,
    ...common.fontWeight500,
    color: theme.palette.grey[700],
    lineHeight: '24px',
  },
  followers: {
    ...common.smallerText,
    ...common.fontWeight500,
    color: theme.palette.grey[700],
    lineHeight: '24px',
  },
  followersList: {
    ...common.flexColumn,
    flex: 1,
    height: 120,
    overflowY: 'auto',
    gap: '4px',
    padding: '8px 0',
    position: 'relative',
  },
  followerContainer: {
    width: '100%',
    minHeight: '40px',
    ...common.flexInline,
    alignItems: 'center',
  },
  followerLeftColumn: {
    ...common.flexColumn,
    flexGrow: 1,
  },
  followerRightColumn: {
    width: '100px',
    ...common.flexInline,
    ...common.justifyEnd,
    alignItems: 'center',
  },
  followerCompany: {
    ...common.middleText,
    ...common.fontWeight500,
    lineHeight: '1rem',
  },
  followerEmail: {
    ...common.smallerText,
    ...common.fontWeight500,
    lineHeight: '1rem',
  },
  followerAction: {
    ...common.smallerText,
    color: theme.palette.primary.main,
    ...common.baseButton,
  },
  shareLink: {
    ...common.middleText,
    color: theme.palette.primary.main,
    ...common.baseButton,
  },
}));

interface FollowerCardProps {
  bookingInquiry: InquiryDetail;
  viewer: InquiryViewer;
  canDelete: boolean;
}

export const FollowerCard: React.FC<FollowerCardProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { bookingInquiry, viewer, canDelete } = props;
  const { can_view_type, company_name, email, id } = viewer;

  const viewerDelete = useBookingInquiryViewerDelete(bookingInquiry.d2d_no, id!);

  const canViewName = () => {
    switch (can_view_type) {
      case 'creator':
        return bookingInquiryStrings.creator;
      case 'shipper':
        return bookingInquiryStrings.shipper;
      case 'consignee':
        return bookingInquiryStrings.consignee;
      case 'owner':
        return bookingInquiryStrings.owner;
      default:
        return undefined;
    }
  };

  return (
    <div>
      <SpinnerModal open={viewerDelete.isLoading} />
      <div className={classes.followerContainer}>
        <div className={classes.followerLeftColumn}>
          <div className={classes.followerCompany}>{company_name || strings.notRegistered}</div>
          <div className={classes.followerEmail}>{email}</div>
        </div>
        <div className={classes.followerRightColumn}>
          {can_view_type !== 'follower' ? (
            <div className={classes.followerAction}>{canViewName()}</div>
          ) : (
            canDelete && (
              <Button
                type={'button'}
                size={'smaller'}
                color={'outlined'}
                onClick={async () => await viewerDelete.mutateAsync()}
                text={<div className={classes.smallerText}>{strings.delete}</div>}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export interface BookingInquiryShareDialogProps extends ContainerProps {
  inquiry: InquiryDetail;
}

export const BookingInquiryShareDialog: React.FC<BookingInquiryShareDialogProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { inquiry, ...rest } = props;
  const { d2d_no, can_views } = inquiry;

  const user = useUser();
  const viewerCreate = useBookingInquiryViewerCreate(d2d_no);

  const initialValues = useMemo(() => {
    return {
      email: '',
    };
  }, []);

  const isCreator = !!inquiry.can_views.find(
    (canView) => canView.user === user.data?.id && canView.can_view_type === 'creator',
  );

  return (
    <Formik
      initialValues={initialValues}
      validate={createValidate(inquiryViewerAddForm)}
      onSubmit={async (values) => {
        await viewerCreate.mutateAsync(unwrap(inquiryViewerAddForm, values).email);
      }}
    >
      {({ handleSubmit }) => {
        return (
          <form onSubmit={handleSubmit}>
            <SubmittingOverlay />
            <ActionableDialog {...rest}>
              <ActionableDialogTitle>
                <span className={classes.title}>{strings.title}</span>
              </ActionableDialogTitle>
              <ActionableDialogContent>
                <div className={classes.container}>
                  <Alert open={Boolean(viewerCreate.error)} severity="error">
                    {viewerCreate.error instanceof Error
                      ? viewerCreate.error.message
                      : String(viewerCreate.error)}
                  </Alert>

                  <Alert open={viewerCreate.data instanceof CodeError} severity="error">
                    {viewerCreate.data && strings.viewerCreateErrors()[viewerCreate.data?.code]}
                  </Alert>

                  <VerticalSpace height={30} />
                  <div className={classes.emailInputContainer}>
                    <Field
                      name="email"
                      className={clsx(classes.emailInput)}
                      placeholder={strings.emailInputPlaceholder}
                      disabled={!isCreator}
                    />
                    <button
                      type="submit"
                      className={classes.shareButton}
                      disabled={!isCreator}
                      title={!isCreator ? strings.creatorOnly : undefined}
                    >
                      {strings.share}
                    </button>
                  </div>
                  <div className={classes.shareLimitation}>{strings.limitation}</div>
                  <VerticalSpace height={12} />
                  <div className={classes.followers}>{strings.followers}</div>
                  <div style={{ position: 'relative' }}>
                    <div className={classes.followersList}>
                      {can_views?.map((viewer, i) => (
                        <FollowerCard
                          key={i}
                          bookingInquiry={inquiry}
                          viewer={viewer}
                          canDelete={isCreator}
                        />
                      ))}
                      <div style={{ height: 40, flexShrink: 0 }} />
                    </div>

                    <div
                      style={{
                        backgroundImage:
                          'linear-gradient(to top, rgba(255,255,255,1), rgba(0,0,0,0))',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 40,
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                </div>
              </ActionableDialogContent>
              <ActionableDialogActions justifyContent="left">
                <div
                  onClick={() => {
                    const copyUrl = `${window.location.href}&shared=true`;
                    navigator.clipboard.writeText(copyUrl).then(
                      () => {
                        // eslint-disable-next-line no-alert
                        alert(strings.copyShareLinkSuccess);
                      },
                      () => {},
                    );
                  }}
                  className={classes.shareLink}
                >
                  {strings.copyShareLink}
                </div>
              </ActionableDialogActions>
            </ActionableDialog>
          </form>
        );
      }}
    </Formik>
  );
};
